import React from 'react';
import { Zap, Gauge } from 'lucide-react';
import { Language, PaceOrSpeedMode } from '../../types';
import { themeMap } from '../../utils/calcTheme';
import { t } from '../../utils/i18n';
import StepperField from '../StepperField';

interface QuickPace {
  label: string;
  m: number;
  s: number;
}

const SPEED_PRESETS_KM = [8, 9, 10, 11, 12, 13, 14, 15, 16, 18];
const SPEED_PRESETS_MILES = [5.0, 5.5, 6.0, 6.5, 7.0, 7.5, 8.0, 8.5, 9.0, 10.0];

interface PaceInputCardProps {
  language: Language;
  isMiles: boolean;
  paceOrSpeedMode: PaceOrSpeedMode;
  displaySpeed: number;
  displaySpeedUnit: string;
  displayPaceUnit: string;
  paceMinutes: number;
  paceSeconds: number;
  paceMileMs: { minutes: number; seconds: number };
  quickPacesKm: QuickPace[];
  quickPacesMiles: QuickPace[];
  onPaceChangeKm: (min: number, sec: number) => void;
  onPaceChangeMiles: (min: number, sec: number) => void;
  onSpeedChange: (speed: number) => void;
  triggerHaptic?: (duration?: number) => void;
}

/**
 * 2. Pace / Snelheid invoerkaart (Input)
 * - Oppervlakte: bg-slate-50 border border-slate-100 rounded-2xl
 * - Icoon-container: bg-blue-100 text-blue-600
 * - Actieve preset: bg-blue-500 text-white shadow-sm
 * - Inactieve presets: bg-slate-200 text-slate-600
 */
export default function PaceInputCard({
  language,
  isMiles,
  paceOrSpeedMode,
  displaySpeed,
  displaySpeedUnit,
  displayPaceUnit,
  paceMinutes,
  paceSeconds,
  paceMileMs,
  quickPacesKm,
  quickPacesMiles,
  onPaceChangeKm,
  onPaceChangeMiles,
  onSpeedChange,
  triggerHaptic = () => {},
}: PaceInputCardProps) {
  const styles = themeMap.pace.inputCard;

  const roundedSpeed = Math.round(displaySpeed * 10) / 10;
  const speedWhole = Math.floor(roundedSpeed);
  const speedTenths = Math.round((roundedSpeed - speedWhole) * 10);

  return (
    <div
      id="form-card-pace"
      className={`p-5 sm:p-6 transition-all space-y-4 ${styles.container}`}
    >
      <div className="flex items-center gap-2.5">
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${styles.iconContainer}`}>
          {paceOrSpeedMode === 'speed' ? (
            <Gauge className="w-4 h-4 stroke-[2.4]" />
          ) : (
            <Zap className="w-4 h-4 stroke-[2.4]" />
          )}
        </div>
        <div>
          <span className="text-sm font-bold text-slate-900 dark:text-slate-100 block">
            {paceOrSpeedMode === 'speed' ? `${t(language, 'speedCardTitle')} (${displaySpeedUnit})` : `Pace (${isMiles ? 'min/mi' : 'min/km'})`}
          </span>
        </div>
      </div>

      {paceOrSpeedMode === 'speed' ? (
        <div className="space-y-3">
          {/* Stepper Columns for Speed */}
          <div className="flex items-center gap-3">
            <StepperField
              idPrefix="speed-whole"
              unit={displaySpeedUnit}
              value={speedWhole}
              onIncrement={() => onSpeedChange(Math.min(50, Math.round((roundedSpeed + 1) * 10) / 10))}
              onDecrement={() => onSpeedChange(Math.max(1, Math.round((roundedSpeed - 1) * 10) / 10))}
              onChange={(val) => {
                if (val % 1 !== 0) {
                  onSpeedChange(Math.max(1, Math.min(50, val)));
                } else {
                  onSpeedChange(Math.max(1, Math.min(50, val + speedTenths / 10)));
                }
              }}
              min={1}
              max={50}
              colorScheme="neutral"
            />

            <StepperField
              idPrefix="speed-tenths"
              unit={t(language, 'unitDecimals')}
              value={speedTenths}
              displayValue={speedTenths.toString()}
              onIncrement={() => onSpeedChange(Math.min(50, Math.round((roundedSpeed + 0.1) * 10) / 10))}
              onDecrement={() => onSpeedChange(Math.max(1, Math.round((roundedSpeed - 0.1) * 10) / 10))}
              onChange={(val) => {
                const cleanTenths = Math.min(9, Math.max(0, val % 10));
                onSpeedChange(Math.max(1, Math.min(50, speedWhole + cleanTenths / 10)));
              }}
              min={0}
              max={9}
              colorScheme="neutral"
            />
          </div>

          {/* Quick speed nudge buttons */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 pt-0.5 scrollbar-none">
            {[-1, -0.5, 0.5, 1].map((delta) => (
              <button
                key={delta}
                id={`btn-speed-nudge-${delta > 0 ? `plus-${delta}` : `minus-${Math.abs(delta)}`}`}
                type="button"
                onClick={() => {
                  triggerHaptic(10);
                  onSpeedChange(Math.max(1, Math.min(50, Math.round((roundedSpeed + delta) * 10) / 10)));
                }}
                className="px-2.5 py-1.5 rounded-xl text-xs font-bold font-mono transition-all cursor-pointer select-none bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 active:scale-95 text-slate-700 dark:text-slate-300 whitespace-nowrap"
              >
                {delta > 0 ? `+${delta}` : delta} {displaySpeedUnit}
              </button>
            ))}
          </div>

          {/* Speed Presets */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 pt-0.5 scrollbar-none">
            {(isMiles ? SPEED_PRESETS_MILES : SPEED_PRESETS_KM).map((speed) => {
              const isMatch = Math.abs(roundedSpeed - speed) < 0.08;
              return (
                <button
                  key={speed}
                  id={`btn-speed-preset-${speed}`}
                  type="button"
                  onClick={() => {
                    triggerHaptic(10);
                    onSpeedChange(speed);
                  }}
                  className={`shrink-0 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer select-none whitespace-nowrap ${
                    isMatch ? styles.activePreset : styles.inactivePreset
                  }`}
                >
                  {speed % 1 === 0 ? `${speed} ${displaySpeedUnit}` : `${speed.toFixed(1)} ${displaySpeedUnit}`}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Stepper Columns for Pace */}
          {isMiles ? (
            <div className="flex items-center gap-3">
              <StepperField
                idPrefix="pace-mi-m"
                unit={t(language, 'unitMin')}
                value={paceMileMs.minutes}
                onIncrement={() => onPaceChangeMiles(paceMileMs.minutes + 1, paceMileMs.seconds)}
                onDecrement={() => onPaceChangeMiles(Math.max(3, paceMileMs.minutes - 1), paceMileMs.seconds)}
                onChange={(val) => onPaceChangeMiles(val, paceMileMs.seconds)}
                min={3}
                max={30}
                colorScheme="neutral"
              />

              <StepperField
                idPrefix="pace-mi-s"
                unit={t(language, 'unitSec')}
                value={paceMileMs.seconds}
                onIncrement={() => {
                  if (paceMileMs.seconds >= 59) {
                    onPaceChangeMiles(paceMileMs.minutes + 1, 0);
                  } else {
                    onPaceChangeMiles(paceMileMs.minutes, paceMileMs.seconds + 1);
                  }
                }}
                onDecrement={() => {
                  if (paceMileMs.seconds <= 0 && paceMileMs.minutes > 3) {
                    onPaceChangeMiles(paceMileMs.minutes - 1, 59);
                  } else {
                    onPaceChangeMiles(paceMileMs.minutes, Math.max(0, paceMileMs.seconds - 1));
                  }
                }}
                onChange={(val) => onPaceChangeMiles(paceMileMs.minutes, val)}
                min={0}
                max={59}
                colorScheme="neutral"
              />
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <StepperField
                idPrefix="pace-m"
                unit={t(language, 'unitMin')}
                value={paceMinutes}
                onIncrement={() => onPaceChangeKm(paceMinutes + 1, paceSeconds)}
                onDecrement={() => onPaceChangeKm(Math.max(2, paceMinutes - 1), paceSeconds)}
                onChange={(val) => onPaceChangeKm(val, paceSeconds)}
                min={2}
                max={20}
                colorScheme="neutral"
              />

              <StepperField
                idPrefix="pace-s"
                unit={t(language, 'unitSec')}
                value={paceSeconds}
                onIncrement={() => {
                  if (paceSeconds >= 59) {
                    onPaceChangeKm(paceMinutes + 1, 0);
                  } else {
                    onPaceChangeKm(paceMinutes, paceSeconds + 1);
                  }
                }}
                onDecrement={() => {
                  if (paceSeconds <= 0 && paceMinutes > 2) {
                    onPaceChangeKm(paceMinutes - 1, 59);
                  } else {
                    onPaceChangeKm(paceMinutes, Math.max(0, paceSeconds - 1));
                  }
                }}
                onChange={(val) => onPaceChangeKm(paceMinutes, val)}
                min={0}
                max={59}
                colorScheme="neutral"
              />
            </div>
          )}

          {/* Presets directly under the pace inputs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 pt-0.5 scrollbar-none">
            {(isMiles ? quickPacesMiles : quickPacesKm).map((qp) => {
              const isMatch = isMiles
                ? paceMileMs.minutes === qp.m && paceMileMs.seconds === qp.s
                : paceMinutes === qp.m && paceSeconds === qp.s;
              return (
                <button
                  key={qp.label}
                  id={`btn-quick-pace-${qp.label.replace(':', '-')}`}
                  type="button"
                  onClick={() => {
                    triggerHaptic(10);
                    if (isMiles) {
                      onPaceChangeMiles(qp.m, qp.s);
                    } else {
                      onPaceChangeKm(qp.m, qp.s);
                    }
                  }}
                  className={`shrink-0 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer select-none whitespace-nowrap ${
                    isMatch ? styles.activePreset : styles.inactivePreset
                  }`}
                >
                  {qp.label}{displayPaceUnit}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
